import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button, Card, Col, Modal, Row, Select, Space, Tag, Tooltip, Typography, message } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { AiOutlineCloseCircle } from "react-icons/ai"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import { gen, getActingUserRequestFields, IClub, swimminglyApi } from "./utils"
import { IClubSeasonInformation } from "./MeetActionsModal"

const { Text } = Typography

interface IVirtualOrganizationGroup {
  organizationId: number
  organizationName: string
  clubs: IClub[]
}

interface IVirtualClubSelection {
  club: IClub
  season: { seasonId: number; seasonName: string } | null
  status: "accepted" | "pending" | "declined" | "new"
}

interface IEditableVirtualMeetParticipant {
  clubId: number
  status: "accepted" | "pending" | "declined" | "removed"
  seasonId: number | null
  seasonName: string | null
  clubName: string | null
  clubNameLong: string | null
  leagueName: string | null
}

interface IUpdateMeetParticipatingClubsVirtualProps {
  visible: boolean
  setVisible: (newVisible: boolean) => void
  meetId?: number | null
  virtualMeetId: number
  homeClubId?: number | null
  meetClubSeasons?: IClubSeasonInformation[]
  refresh: () => void
}

const getClubDisplayName = (club: IClub) =>
  club.nameLong || club.name_long || club.name || `Club ${club.clubId || ""}`

const sectionTitleStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: "#595959",
  textTransform: "uppercase" as const,
  letterSpacing: 0.6,
}

const MEET_ACTIONS_CHILD_MODAL_Z_INDEX = 1100

const getClubSearchLabel = (club: IClub) =>
  [
    getClubDisplayName(club),
    club.name,
    club.leagueName,
    club.organizationNames,
  ]
    .filter(Boolean)
    .join(" ")

const getStatusTagColor = (status: IVirtualClubSelection["status"], locked: boolean) => {
  if (locked) return "blue"
  if (status === "accepted") return "green"
  if (status === "declined") return "default"
  if (status === "pending") return "gold"
  return "purple"
}

const getStatusLabel = (status: IVirtualClubSelection["status"], locked: boolean) => {
  if (locked) return "Organizer club"
  if (status === "accepted") return "Accepted"
  if (status === "declined") return "Declined"
  if (status === "pending") return "Pending invitation"
  return "New invitation"
}

const getStatusDescription = (selection: IVirtualClubSelection, locked: boolean) => {
  if (locked) {
    return selection.season?.seasonName
      ? `Roster: ${selection.season.seasonName}`
      : "Your club already owns this shell."
  }

  if (selection.status === "accepted") {
    return selection.season?.seasonName
      ? `Roster chosen by club: ${selection.season.seasonName}`
      : "Roster already chosen by club."
  }

  if (selection.status === "declined") {
    return "Keep this club selected to send a fresh invitation when you save."
  }

  if (selection.status === "pending") {
    return "This club will choose its own roster and pool when it accepts."
  }

  return "This club will receive a new invitation when you save."
}

function VirtualClubSelectionRow({
  selection,
  clubSelectionsInMeet,
  availableClubs,
  leagueClubs,
  organizationGroups,
  locked,
  onSwapClub,
  onRemoveClub,
}: {
  selection: IVirtualClubSelection
  clubSelectionsInMeet: IVirtualClubSelection[]
  availableClubs: IClub[]
  leagueClubs: IClub[]
  organizationGroups: IVirtualOrganizationGroup[]
  locked: boolean
  onSwapClub: (oldClubId: number, newClubId: number) => void
  onRemoveClub: (clubId: number) => void
}) {
  const [clubSearchValue, setClubSearchValue] = useState("")

  const selectedClubId =
    selection.club.clubId && selection.club.clubId > 0
      ? selection.club.clubId
      : undefined

  const clubOptions = useMemo(() => {
    const selectedIds = new Set(
      clubSelectionsInMeet
        .map((clubSelection) => clubSelection.club.clubId)
        .filter((clubId): clubId is number => typeof clubId === "number" && clubId > 0),
    )

    if (selectedClubId) {
      selectedIds.delete(selectedClubId)
    }

    const filteredClubs = availableClubs.filter(
      (clubOption) => !selectedIds.has(clubOption.clubId || -1),
    )
    const leagueClubIds = new Set(
      leagueClubs
        .map((clubOption) => clubOption.clubId)
        .filter((clubId): clubId is number => typeof clubId === "number"),
    )
    const groupedOrganizationClubs = organizationGroups.map((organization) => ({
      organizationId: organization.organizationId,
      organizationName: organization.organizationName,
      clubs: filteredClubs.filter((clubOption) =>
        organization.clubs.some(
          (organizationClub) => organizationClub.clubId === clubOption.clubId,
        ),
      ),
    }))

    const sameLeague: IClub[] = []
    const others: IClub[] = []

    filteredClubs.forEach((clubOption) => {
      const clubId = clubOption.clubId
      const isSameLeague = typeof clubId === "number" && leagueClubIds.has(clubId)

      if (isSameLeague) {
        sameLeague.push(clubOption)
        return
      }

      others.push(clubOption)
    })

    const normalizedSearch = clubSearchValue.trim().toLowerCase()
    if (normalizedSearch) {
      return filteredClubs.filter((clubOption) =>
        getClubSearchLabel(clubOption).toLowerCase().includes(normalizedSearch),
      )
    }

    return { sameLeague, organizationGroups: groupedOrganizationClubs, others }
  }, [
    availableClubs,
    clubSelectionsInMeet,
    selectedClubId,
    leagueClubs,
    organizationGroups,
    clubSearchValue,
  ])

  const renderClubOption = (clubOption: IClub) => (
    <Select.Option
      key={`virtual_meet_club_option_${selection.club.clubId || -1}_${clubOption.clubId}`}
      value={clubOption.clubId || -1}
    >
      {getClubDisplayName(clubOption)}
    </Select.Option>
  )

  const leagueGroupLabel =
    leagueClubs.find((clubOption) => clubOption.leagueName)?.leagueName ||
    leagueClubs.find((clubOption) => clubOption.league)?.league ||
    "Clubs In Your League"

  return (
    <Card size="small" style={{ borderRadius: 12 }} styles={{ body: { padding: 10 } }}>
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} md={10}>
          <Select
            value={selectedClubId}
            size="middle"
            style={{ width: "100%" }}
            placeholder="Choose Club"
            disabled={locked}
            onOpenChange={(open) => {
              if (!open) {
                setClubSearchValue("")
              }
            }}
            onChange={(newClubId) => onSwapClub(selection.club.clubId || -1, newClubId)}
            showSearch={{
              filterOption: (inputVal: string, option: any) => {
                const raw = option?.children ?? option?.label ?? option?.value ?? ""
                const text = Array.isArray(raw) ? raw.join(" ") : String(raw)
                return text
                  .toLowerCase()
                  .replace(/\s+/g, "")
                  .includes(String(inputVal).toLowerCase().replace(/\s+/g, ""))
              },
              onSearch: setClubSearchValue,
              optionFilterProp: "children",
            }}
          >
            {Array.isArray(clubOptions) ? (
              clubOptions.map(renderClubOption)
            ) : (
              <>
                {clubOptions.sameLeague.length > 0 ? (
                  <Select.OptGroup label={leagueGroupLabel}>
                    {clubOptions.sameLeague.map(renderClubOption)}
                  </Select.OptGroup>
                ) : null}
                {clubOptions.organizationGroups.map((organizationGroup) =>
                  organizationGroup.clubs.length > 0 ? (
                    <Select.OptGroup
                      key={`virtual_org_group_${organizationGroup.organizationId}`}
                      label={organizationGroup.organizationName}
                    >
                      {organizationGroup.clubs.map(renderClubOption)}
                    </Select.OptGroup>
                  ) : null,
                )}
                {clubOptions.others.length > 0 ? (
                  <Select.OptGroup label="Other Clubs">
                    {clubOptions.others.map(renderClubOption)}
                  </Select.OptGroup>
                ) : null}
              </>
            )}
          </Select>
        </Col>
        <Col xs={24} md={11}>
          <Space orientation="vertical" size={4} style={{ width: "100%" }}>
            <Tag color={getStatusTagColor(selection.status, locked)}>
              {getStatusLabel(selection.status, locked)}
            </Tag>
            <Text type="secondary">{getStatusDescription(selection, locked)}</Text>
          </Space>
        </Col>
        <Col xs={24} md={3}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            {locked ? (
              <Tag color="blue">Primary club</Tag>
            ) : (
              <Tooltip title="Remove this club from the virtual meet">
                <Button
                  danger
                  type="text"
                  shape="circle"
                  aria-label="Remove club from virtual meet"
                  icon={<AiOutlineCloseCircle style={{ fontSize: "1.35rem" }} />}
                  onClick={() => onRemoveClub(selection.club.clubId || -1)}
                />
              </Tooltip>
            )}
          </div>
        </Col>
      </Row>
    </Card>
  )
}

const mapParticipantToSelection = (
  participant: IEditableVirtualMeetParticipant,
  clubs: IClub[],
): IVirtualClubSelection => {
  const matchingClub = clubs.find((club) => club.clubId === participant.clubId)

  return {
    club:
      matchingClub || {
        clubId: participant.clubId,
        nameLong: participant.clubNameLong || participant.clubName || undefined,
        name: participant.clubName || undefined,
        leagueName: participant.leagueName || undefined,
      },
    season:
      participant.seasonId && participant.seasonName
        ? { seasonId: participant.seasonId, seasonName: participant.seasonName }
        : null,
    status:
      participant.status === "accepted" ||
      participant.status === "pending" ||
      participant.status === "declined"
        ? participant.status
        : "pending",
  }
}

export default function UpdateMeetParticipatingClubsVirtual({
  visible,
  setVisible,
  meetId = null,
  virtualMeetId,
  homeClubId = null,
  refresh,
}: IUpdateMeetParticipatingClubsVirtualProps): JSX.Element {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const aliasedUser = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedUser,
  )
  const actingUser = aliasedUser || user
  const [availableClubs, setAvailableClubs] = useState<IClub[]>([])
  const [leagueClubs, setLeagueClubs] = useState<IClub[]>([])
  const [organizationGroups, setOrganizationGroups] = useState<
    IVirtualOrganizationGroup[]
  >([])
  const [draftClubSelections, setDraftClubSelections] = useState<
    IVirtualClubSelection[]
  >([])
  const [currentClubIds, setCurrentClubIds] = useState<number[]>([])
  const [resolvedHomeClubId, setResolvedHomeClubId] = useState<number | null>(homeClubId)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const nextTempClubIdRef = useRef(-1)

  useEffect(() => {
    if (!visible || !virtualMeetId) {
      return
    }

    let mounted = true
    setLoading(true)

    Promise.all([
      swimminglyApi.get(gen(`/api/getAllClubsRawFormat`)),
      swimminglyApi.get(gen(`/api/getVirtualMeetParticipantsForEdit/${virtualMeetId}`)),
    ])
      .then(([allClubsResp, participantsResp]) => {
        if (!mounted) return

        const allClubs = allClubsResp?.status === "success" ? allClubsResp.clubs || [] : []
        const participants =
          participantsResp?.status === "success" ? participantsResp.participants || [] : []
        const organizerClubIdFromApi =
          participantsResp?.status === "success"
            ? participantsResp.organizerClubId || homeClubId || null
            : homeClubId || null

        setAvailableClubs(allClubs)
        setDraftClubSelections(
          participants.map((participant: IEditableVirtualMeetParticipant) =>
            mapParticipantToSelection(participant, allClubs),
          ),
        )
        setCurrentClubIds(
          participants
            .map((participant: IEditableVirtualMeetParticipant) => participant.clubId)
            .filter((clubId: number) => Number.isInteger(clubId) && clubId > 0),
        )
        setResolvedHomeClubId(organizerClubIdFromApi)
        nextTempClubIdRef.current = -1

        if (!organizerClubIdFromApi) {
          setLeagueClubs([])
          setOrganizationGroups([])
          return
        }

        return Promise.all([
          swimminglyApi.get(gen(`/api/getLeagueClubsForClub/${organizerClubIdFromApi}`)),
          swimminglyApi.get(gen(`/api/getOrganizationClubGroupsForClub/${organizerClubIdFromApi}`)),
        ]).then(([leagueClubsResp, organizationResp]) => {
          if (!mounted) return

          setLeagueClubs(
            leagueClubsResp?.status === "success" ? leagueClubsResp.clubs || [] : [],
          )
          setOrganizationGroups(
            organizationResp?.status === "success"
              ? organizationResp.organizations || []
              : [],
          )
        })
      })
      .catch(() => {
        if (!mounted) return
        message.error("Problem loading virtual meet club options.")
        setAvailableClubs([])
        setLeagueClubs([])
        setOrganizationGroups([])
        setDraftClubSelections([])
      })
      .finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [visible, virtualMeetId, homeClubId])

  const hostClubSelection = draftClubSelections.find(
    (clubSelection) => clubSelection.club.clubId === resolvedHomeClubId,
  )

  const remainingClubs = useMemo(() => {
    const clubsAlreadyInMeet = draftClubSelections.map(
      (clubSelection) => clubSelection.club.clubId || -1,
    )

    return availableClubs.filter(
      (clubOption) => !clubsAlreadyInMeet.includes(clubOption.clubId || -1),
    )
  }, [availableClubs, draftClubSelections])

  const nonHostClubSelections = useMemo(
    () =>
      draftClubSelections.filter(
        (clubSelection) => clubSelection.club.clubId !== resolvedHomeClubId,
      ),
    [draftClubSelections, resolvedHomeClubId],
  )

  const clubsToRemove = useMemo(
    () =>
      currentClubIds.filter(
        (clubId) => !draftClubSelections.some((clubSelection) => clubSelection.club.clubId === clubId),
      ),
    [currentClubIds, draftClubSelections],
  )

  const closeModal = () => {
    if (saving) return
    setVisible(false)
  }

  const addVirtualDraftClub = useCallback(() => {
    setDraftClubSelections((currentSelections) => {
      const selectedClubIds = currentSelections
        .map((clubSelection) => clubSelection.club.clubId)
        .filter((clubId): clubId is number => typeof clubId === "number" && clubId > 0)

      const clubsToSelect = availableClubs.filter(
        (availableClub) => !selectedClubIds.includes(availableClub.clubId || -1),
      )

      if (clubsToSelect.length === 0) {
        return currentSelections
      }

      return [
        ...currentSelections,
        {
          club: {
            clubId: nextTempClubIdRef.current,
            nameLong: "",
            name: "",
          },
          season: null,
          status: "new",
        },
      ]
    })

    nextTempClubIdRef.current -= 1
  }, [availableClubs])

  const removeVirtualDraftClub = useCallback((clubId: number) => {
    setDraftClubSelections((currentSelections) =>
      currentSelections.filter((clubSelection) => clubSelection.club.clubId !== clubId),
    )
  }, [])

  const swapVirtualDraftClub = useCallback(
    (oldClubId: number, newClubId: number) => {
      setDraftClubSelections((currentSelections) => {
        const clubIds = currentSelections.map((clubSelection) => clubSelection.club.clubId)

        if (clubIds.includes(newClubId)) {
          return currentSelections
        }

        const newClub = availableClubs.find(
          (searchClub) => searchClub.clubId === newClubId,
        )
        if (!newClub) {
          return currentSelections
        }

        return currentSelections.map((clubSelection) => {
          if (clubSelection.club.clubId !== oldClubId) {
            return clubSelection
          }

          return {
            club: newClub,
            season: null,
            status: "new",
          }
        })
      })
    },
    [availableClubs],
  )

  const submitChanges = () => {
    const invalidRows = draftClubSelections.filter(
      (clubSelection) => !clubSelection.club.clubId || clubSelection.club.clubId < 1,
    )

    if (invalidRows.length > 0) {
      message.error("Choose a club for every participating-team row.")
      return
    }

    setSaving(true)
    swimminglyApi
      .post(gen("/api/updateVirtualMeetParticipatingClubs"))
      .body({
        meetId,
        virtualMeetId,
        ...getActingUserRequestFields(actingUser),
        clubSeasons: draftClubSelections.map((clubSelection) => ({
          clubId: clubSelection.club.clubId,
          seasonId: clubSelection.season?.seasonId || null,
          seasonName: clubSelection.season?.seasonName || null,
        })),
      })
      .then((data: { status: string; message?: string }) => {
        if (data.status === "success") {
          message.success(data.message || "Virtual meet participating clubs updated successfully.")
          refresh()
          setVisible(false)
          return
        }

        if (data.status === "warning") {
          message.warning(
            data.message ||
              "Virtual meet participating clubs updated, but one or more SwimminglyFan syncs failed.",
          )
          refresh()
          setVisible(false)
          return
        }

        message.error(data.message || "Problem updating virtual meet participating clubs.")
      })
      .catch(() => {
        message.error("Problem updating virtual meet participating clubs.")
      })
      .finally(() => setSaving(false))
  }

  const onSave = () => {
    if (draftClubSelections.length === 0) {
      message.error("At least one participating club is required.")
      return
    }

    if (clubsToRemove.length === 0) {
      submitChanges()
      return
    }

    Modal.confirm({
      title: "Remove clubs from this virtual meet?",
      content:
        "Removing a club here will withdraw pending or declined invitations, and accepted clubs will be detached from the virtual meet while keeping their standalone meet. Clubs with entries, heat-sheet activity, declarations activity, or results will still be blocked.",
      okText: "Confirm changes",
      cancelText: "Cancel",
      zIndex: MEET_ACTIONS_CHILD_MODAL_Z_INDEX + 10,
      onOk: submitChanges,
    })
  }

  return (
    <Modal
      title="Update Virtual Meet Participating Clubs"
      open={visible}
      zIndex={MEET_ACTIONS_CHILD_MODAL_Z_INDEX}
      onCancel={closeModal}
      width={860}
      footer={[
        <Button key="cancel" onClick={closeModal} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={onSave} loading={saving}>
          Save
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
          <Text type="secondary">Loading participating clubs...</Text>
        </div>
      ) : (
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <Text type="secondary">
            Accepted clubs keep their chosen roster. Pending and new invitees will choose their own roster and pool when they accept.
          </Text>
          <div style={{ display: "grid", gap: 12 }}>
            <div style={sectionTitleStyle}>Currently Included In This Virtual Meet</div>
            {hostClubSelection ? (
              <VirtualClubSelectionRow
                selection={hostClubSelection}
                clubSelectionsInMeet={draftClubSelections}
                availableClubs={availableClubs}
                leagueClubs={leagueClubs}
                organizationGroups={organizationGroups}
                locked={true}
                onSwapClub={swapVirtualDraftClub}
                onRemoveClub={removeVirtualDraftClub}
              />
            ) : null}
            {nonHostClubSelections.map((clubSelection) => (
              <VirtualClubSelectionRow
                key={`virtual_edit_club_${clubSelection.club.clubId}_${clubSelection.status}`}
                selection={clubSelection}
                clubSelectionsInMeet={draftClubSelections}
                availableClubs={availableClubs}
                leagueClubs={leagueClubs}
                organizationGroups={organizationGroups}
                locked={false}
                onSwapClub={swapVirtualDraftClub}
                onRemoveClub={removeVirtualDraftClub}
              />
            ))}
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            <div style={sectionTitleStyle}>Invite Another Team</div>
            {remainingClubs.length > 0 ? (
              <Button
                type="dashed"
                size="large"
                block
                icon={<PlusOutlined />}
                onClick={addVirtualDraftClub}
              >
                Add Another Team
              </Button>
            ) : (
              <Card size="small" style={{ borderRadius: 12 }}>
                <Text type="secondary">
                  No additional clubs are available to add to this virtual meet.
                </Text>
              </Card>
            )}
          </div>
        </Space>
      )}
    </Modal>
  )
}
